// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import MuiLink from "@mui/material/Link";

// Material Kit 2 React components
import MKBox from "components/MKBox";

const getYouTubeVideoId = (url) => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
    /youtube\.com\/embed\/([^&\s]+)/,
    /youtube\.com\/v\/([^&\s]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

const getYouTubeThumbnail = (videoId, quality = "maxresdefault") => {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

function TransparentBlogCard({
  image,
  action,
  maxWidth = 400,
  maxHeight = 300,
}) {
  const youtubeVideoId = action?.route ? getYouTubeVideoId(action.route) : null;
  const finalImage = youtubeVideoId
    ? getYouTubeThumbnail(youtubeVideoId)
    : image;

  const imageTemplate = (
    <MKBox position="relative" borderRadius="lg">
      <MKBox
        component="img"
        src={finalImage}
        borderRadius="lg"
        shadow="md"
        width="100%"
        height={maxHeight}
        maxWidth={maxWidth}
        position="relative"
        zIndex={1}
        sx={{
          objectFit: "cover",
        }}
      />
      <MKBox
        borderRadius="lg"
        shadow="md"
        width="100%"
        height="100%"
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        position="absolute"
        left={0}
        top={0}
        sx={{
          backgroundImage: `url(${finalImage})`,
          transform: "scale(0.94)",
          filter: "blur(12px)",
        }}
      />
      {youtubeVideoId && (
        <MKBox
          position="absolute"
          top="50%"
          left="50%"
          zIndex={2}
          sx={{
            transform: "translate(-50%, -50%)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: "50%",
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MKBox
            component="svg"
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="white"
          >
            <path d="M8 5v14l11-7z" />
          </MKBox>
        </MKBox>
      )}
    </MKBox>
  );

  const renderClickableImage = () => {
    if (action.onClick) {
      return (
        <MKBox
          onClick={action.onClick}
          sx={{
            cursor: "pointer",
            "&:hover": {
              opacity: 0.9,
            },
          }}
        >
          {imageTemplate}
        </MKBox>
      );
    }

    if (action.type === "internal") {
      return <Link to={action.route}>{imageTemplate}</Link>;
    }

    return (
      <MuiLink href={action.route} target="_blank" rel="noreferrer">
        {imageTemplate}
      </MuiLink>
    );
  };

  return (
    <Card
      sx={{
        background: "transparent",
        boxShadow: "none",
        overflow: "visible",
        maxWidth: maxWidth,
      }}
    >
      {renderClickableImage()}
    </Card>
  );
}

// Typechecking props for the TransparentBlogCard
TransparentBlogCard.propTypes = {
  image: PropTypes.string.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]),
    route: PropTypes.string,
    color: PropTypes.oneOf([
      "inherit",
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
      "text",
    ]).isRequired,
    onClick: PropTypes.func,
  }).isRequired,
};

export default TransparentBlogCard;
